import { Box, Fade, useMediaQuery, useTheme } from '@mui/material';
import React, { Suspense, useEffect } from 'react';

import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PageLoading } from '@pv/common/components';
import { useVenues } from '../../providers/venues';
import DesktopNavBar from './DesktopNavBar';
import {
  MobileNavBar,
  isMobileNavBarVisible,
  mobileAppbarHeight,
} from './MobileNavBar';
import { LockedOut } from '../../pages/LockedOut';
import { useUserAuth } from '../../providers/auth';
import { gql, useQuery } from '@apollo/client';
import { ServerError } from '../../pages/ServerError';
import { NavLayoutProvider, useNavLayout } from './NavLayoutContext';

const USER_NEEDS_ONBOARDING = gql`
  query UserNeedsOnboarding {
    userNeedsOnboarding
  }
`;

const DashboardInner = () => {
  const { venues, loading, error } = useVenues();
  const { user } = useUserAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const showMobileNavBar = smallScreen && isMobileNavBarVisible(pathname);
  const { headerHeightPx } = useNavLayout();

  const { data } = useQuery(USER_NEEDS_ONBOARDING);
  const userNeedsOnboarding = data?.userNeedsOnboarding;

  const isOnSettings = pathname.includes('settings');
  const lockedOutVenues = venues?.filter((v) => v.subscription.lockOut);

  const hasServerError = !loading && error !== null;

  const isLockedOut =
    !isOnSettings && !hasServerError && lockedOutVenues?.length > 0;

  const paddingBottomPx = showMobileNavBar ? `${mobileAppbarHeight}px` : '0px';

  useEffect(() => {
    if (
      userNeedsOnboarding &&
      user?.activeVenueMemberships &&
      user.activeVenueMemberships.length === 0
    ) {
      navigate('/onboarding');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userNeedsOnboarding, user?.activeVenueMemberships]);

  return (
    <div>
      <DesktopNavBar />
      <Suspense fallback={<PageLoading />}>
        <Fade in={!!user}>
          <main>
            <Box
              sx={{
                paddingTop: `${headerHeightPx}px`,
                paddingBottom: paddingBottomPx,
                '@media print': {
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              }}
            >
              {hasServerError ? (
                <ServerError />
              ) : isLockedOut ? (
                <LockedOut lockedOutVenues={lockedOutVenues} />
              ) : (
                <Outlet />
              )}
            </Box>
          </main>
        </Fade>
      </Suspense>
      {showMobileNavBar && <MobileNavBar />}
    </div>
  );
};

const Dashboard = () => (
  <NavLayoutProvider>
    <DashboardInner />
  </NavLayoutProvider>
);

export default Dashboard;
